// v44: tiered retail pricing for Precision Nasal Mist sales.
// The tier table lives in Mongo (`app_settings.nasal_mist_sale_tiers`) and is
// served through GET /api/config — editable at runtime without code changes.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../api";

export const DEFAULT_NASAL_TIERS = [
  { min_qty: 1, unit_price: 100 },
  { min_qty: 2, unit_price: 95 },
  { min_qty: 3, unit_price: 90 },
  { min_qty: 4, unit_price: 85 },
  { min_qty: 5, unit_price: 80 },
];

let tiersCache = null;

export function useNasalTiers() {
  const [tiers, setTiers] = useState(tiersCache || DEFAULT_NASAL_TIERS);
  useEffect(() => {
    if (tiersCache) return;
    api
      .get("/config")
      .then(({ data }) => {
        if (Array.isArray(data?.nasal_mist_sale_tiers) && data.nasal_mist_sale_tiers.length) {
          tiersCache = data.nasal_mist_sale_tiers;
          setTiers(tiersCache);
        }
      })
      .catch(() => {});
  }, []);
  return tiers;
}

export function isNasalMist(product) {
  if (!product) return false;
  const cat = (product.category || "").toLowerCase();
  if (cat === "precision nasal mist" || cat === "nasal_sprays") return true;
  return /nasal/i.test(product.name || "");
}

// v400.64: only the Elite 10 peptides can be converted to a Precision Nasal Mist.
export const ELITE_NASAL_BASES = [
  "semax", "selank", "pinealon", "oxytocin", "dsip",
  "pt-141", "nad+", "mots-c", "ss-31", "aod-9604",
];

export function isEliteConvertible(product) {
  if (!product) return false;
  if ((product.category || "Injectable") !== "Injectable") return false;
  const n = (product.name || "").trim().toLowerCase();
  return ELITE_NASAL_BASES.some((b) => n.startsWith(b));
}

export function tierUnitPrice(tiers, qty) {
  const n = Math.max(1, Number(qty) || 1);
  let price = null;
  for (const t of [...tiers].sort((a, b) => a.min_qty - b.min_qty)) {
    if (n >= t.min_qty) price = t.unit_price;
  }
  return price;
}

export function TierBreakdown({ tiers, qty }) {
  const { t } = useTranslation();
  const n = Math.max(1, Number(qty) || 1);
  const sorted = [...tiers].sort((a, b) => a.min_qty - b.min_qty);
  const activePrice = tierUnitPrice(sorted, n);
  return (
    <div
      className="rounded-lg border border-[#c9a24a] bg-[#fbf6e7]/60 p-3"
      data-testid="nasal-tier-breakdown"
    >
      <p className="text-[11px] font-semibold text-[#8a6a1c] uppercase tracking-wider mb-1.5">
        {t("tiers.label")}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {sorted.map((tier, i) => {
          const isLast = i === sorted.length - 1;
          const active = tier.unit_price === activePrice &&
            (isLast ? n >= tier.min_qty : n >= tier.min_qty && n < sorted[i + 1].min_qty);
          return (
            <span
              key={tier.min_qty}
              data-testid={`tier-chip-${tier.min_qty}`}
              className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border tabular-nums ${
                active
                  ? "bg-[#c9a24a] border-[#c9a24a] text-zinc-950"
                  : "bg-white border-zinc-200 text-zinc-600"
              }`}
            >
              {tier.min_qty}
              {isLast ? "+" : ""}: ${tier.unit_price}
            </span>
          );
        })}
      </div>
      {activePrice != null && (
        <p className="text-[11px] text-[#8a6a1c] mt-1.5" data-testid="tier-applied-line">
          {t("tiers.applied", { n, price: activePrice, total: (n * activePrice).toFixed(2) })}
        </p>
      )}
    </div>
  );
}
